import { TERMS_SCENE } from "../config/terms-scene.js";

const TAU = Math.PI * 2;
const CYCLE_MS = TERMS_SCENE.timing.cycleMs;

function clamp(value, minimum = 0, maximum = 1) {
  return Math.max(minimum, Math.min(maximum, value));
}

function smoothstep(value) {
  const x = clamp(value);
  return x * x * (3 - 2 * x);
}

function seeded(seed) {
  const value = Math.sin(seed * 127.1 + 311.7) * 43758.5453123;
  return value - Math.floor(value);
}

const PATH_POINTS = TERMS_SCENE.path;

function catmullRom(a, b, c, d, t) {
  const t2 = t * t;
  const t3 = t2 * t;
  return 0.5 * ((2 * b) + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
}

function pathPoint(progress, width, height) {
  const normalized = clamp(progress) * (PATH_POINTS.length - 1);
  const segment = Math.min(PATH_POINTS.length - 2, Math.floor(normalized));
  const t = normalized - segment;
  const a = PATH_POINTS[Math.max(0, segment - 1)];
  const b = PATH_POINTS[segment];
  const c = PATH_POINTS[Math.min(PATH_POINTS.length - 1, segment + 1)];
  const d = PATH_POINTS[Math.min(PATH_POINTS.length - 1, segment + 2)];
  return {
    x: catmullRom(a.x, b.x, c.x, d.x, t) * width,
    y: catmullRom(a.y, b.y, c.y, d.y, t) * height,
  };
}

function pathFrame(progress, width, height) {
  const point = pathPoint(progress, width, height);
  const before = pathPoint(progress - 0.0025, width, height);
  const after = pathPoint(progress + 0.0025, width, height);
  const magnitude = Math.max(0.001, Math.hypot(after.x - before.x, after.y - before.y));
  return {
    ...point,
    tx: (after.x - before.x) / magnitude,
    ty: (after.y - before.y) / magnitude,
    nx: -(after.y - before.y) / magnitude,
    ny: (after.x - before.x) / magnitude,
  };
}

function cycleState(elapsed) {
  const cycle = (Math.max(0, elapsed) % CYCLE_MS) / CYCLE_MS;
  const { restEnd, chargeEnd, traverseEnd, holdEnd, decayEnd } = TERMS_SCENE.timing;
  if (cycle < restEnd) return { phase: "rest", progress: 0.015, energy: 0, residue: 0 };
  if (cycle < chargeEnd) {
    const local = smoothstep((cycle - restEnd) / (chargeEnd - restEnd));
    return { phase: "charge", progress: 0.015, energy: local * 0.78, residue: local * 0.18 };
  }
  if (cycle < traverseEnd) {
    const local = smoothstep((cycle - chargeEnd) / (traverseEnd - chargeEnd));
    return { phase: "traverse", progress: 0.015 + local * 0.97, energy: 0.78 + Math.sin(local * Math.PI) * 0.22, residue: 0.82 };
  }
  if (cycle < holdEnd) {
    const local = (cycle - traverseEnd) / (holdEnd - traverseEnd);
    return { phase: "hold", progress: 0.985, energy: 1 - local * 0.15, residue: 0.92 };
  }
  if (cycle < decayEnd) {
    const local = smoothstep((cycle - holdEnd) / (decayEnd - holdEnd));
    return { phase: "decay", progress: 0.985, energy: (1 - local) * 0.84, residue: 0.92 * (1 - local) };
  }
  return { phase: "settle", progress: 0.985, energy: 0, residue: 0 };
}

function drawVolumetricResponse(context, width, height, state) {
  if (state.energy <= 0.002) return;
  const head = pathFrame(state.progress, width, height);
  const reach = Math.max(width, height) * 0.34;
  const sourceX = head.x - head.tx * reach;
  const sourceY = head.y - head.ty * reach;
  const spread = Math.max(70, Math.min(width, height) * 0.14);

  context.save();
  context.globalCompositeOperation = "screen";
  const beam = context.createLinearGradient(sourceX, sourceY, head.x, head.y);
  beam.addColorStop(0, "rgba(26,94,126,0)");
  beam.addColorStop(0.5, `rgba(76,183,220,${0.018 * state.energy})`);
  beam.addColorStop(0.82, `rgba(147,230,250,${0.07 * state.energy})`);
  beam.addColorStop(1, `rgba(232,253,255,${0.17 * state.energy})`);
  context.beginPath();
  context.moveTo(sourceX + head.nx * spread, sourceY + head.ny * spread);
  context.lineTo(head.x + head.nx * 13, head.y + head.ny * 13);
  context.lineTo(head.x - head.nx * 13, head.y - head.ny * 13);
  context.lineTo(sourceX - head.nx * spread, sourceY - head.ny * spread);
  context.closePath();
  context.filter = "blur(18px)";
  context.fillStyle = beam;
  context.fill();
  context.filter = "none";

  const radius = Math.max(width, height) * 0.28;
  const response = context.createRadialGradient(head.x, head.y, 0, head.x, head.y, radius);
  response.addColorStop(0, `rgba(224,251,255,${0.22 * state.energy})`);
  response.addColorStop(0.08, `rgba(137,226,250,${0.14 * state.energy})`);
  response.addColorStop(0.32, `rgba(43,139,182,${0.055 * state.energy})`);
  response.addColorStop(1, "rgba(0,26,44,0)");
  context.fillStyle = response;
  context.fillRect(head.x - radius, head.y - radius, radius * 2, radius * 2);
  context.restore();
}

function drawPathStroke(context, width, height, state, elapsed, layer) {
  const wake = state.phase === "charge" ? 0.035 : state.phase === "traverse" ? 0.23 : 0.3;
  const start = clamp(state.progress - wake);
  context.save();
  context.globalCompositeOperation = "lighter";
  context.lineCap = "round";
  context.lineJoin = "round";
  context.lineWidth = layer.width;
  context.shadowBlur = layer.blur;
  context.shadowColor = layer.shadow;
  context.beginPath();
  let drawing = false;
  for (let index = 0; index < 112; index += 1) {
    const ratio = index / 111;
    const progress = start + (state.progress - start) * ratio;
    const frame = pathFrame(progress, width, height);
    const age = 1 - ratio;
    const frequency = elapsed * 0.0018 + progress * 58 + layer.seed;
    const turbulence = Math.sin(frequency) * 0.58 + Math.sin(frequency * 2.37) * 0.24 + Math.sin(frequency * 4.91) * 0.08;
    const breakup = Math.sin(progress * 167 + layer.seed * 7 + elapsed * 0.0035);
    const offset = turbulence * layer.offset * (0.35 + age * 0.8);
    const x = frame.x + frame.nx * offset;
    const y = frame.y + frame.ny * offset;
    const active = layer.continuous || breakup > -0.52 || ratio > 0.9;
    if (!active || state.residue < 0.02) {
      drawing = false;
      continue;
    }
    if (!drawing) context.moveTo(x, y);
    else context.lineTo(x, y);
    drawing = true;
  }
  context.strokeStyle = `rgba(${layer.rgb},${layer.alpha * state.residue})`;
  context.stroke();
  context.restore();
}

function drawFilamentBranches(context, width, height, state, elapsed) {
  if (state.residue < 0.08) return;
  const start = clamp(state.progress - 0.28);
  context.save();
  context.globalCompositeOperation = "lighter";
  context.lineCap = "round";
  for (let branch = 0; branch < 22; branch += 1) {
    const anchorProgress = start + (state.progress - start) * seeded(branch + 2.4);
    const frame = pathFrame(anchorProgress, width, height);
    const life = 0.5 + 0.5 * Math.sin(elapsed * 0.0062 + branch * 2.13);
    if (life < 0.3) continue;
    const direction = branch % 2 ? 1 : -1;
    const length = (14 + seeded(branch + 11) * 46) * state.residue * life;
    const bend = (seeded(branch + 24) - 0.5) * 24;
    const segments = branch % 3 === 0 ? 4 : 3;
    let lastX = frame.x;
    let lastY = frame.y;
    context.beginPath();
    context.moveTo(frame.x, frame.y);
    for (let segment = 1; segment <= segments; segment += 1) {
      const ratio = segment / segments;
      const jag = (seeded(branch * 17 + segment * 9.3) - 0.5) * (14 - ratio * 5);
      lastX = frame.x + frame.nx * direction * length * ratio + frame.tx * (bend * ratio + jag);
      lastY = frame.y + frame.ny * direction * length * ratio + frame.ty * (bend * ratio + jag);
      context.lineTo(lastX, lastY);
      if (segment === 2 && branch % 4 === 0) {
        const twig = direction * (7 + seeded(branch + 88) * 17);
        context.moveTo(lastX, lastY);
        context.lineTo(lastX + frame.nx * twig + frame.tx * jag * 0.7, lastY + frame.ny * twig + frame.ty * jag * 0.7);
        context.moveTo(lastX, lastY);
      }
    }
    context.lineWidth = branch % 3 === 0 ? 1.3 : 0.72;
    context.shadowBlur = branch % 3 === 0 ? 12 : 6;
    context.shadowColor = "rgba(113,222,255,.8)";
    context.strokeStyle = `rgba(${branch % 4 === 0 ? "244,254,255" : "115,220,248"},${(0.2 + life * 0.52) * state.residue})`;
    context.stroke();
  }
  context.restore();
}

function drawSecondaryMotes(context, width, height, state, elapsed) {
  if (state.residue < 0.035) return;
  context.save();
  context.globalCompositeOperation = "lighter";
  for (let index = 0; index < 38; index += 1) {
    const age = seeded(index + 44.2);
    const progress = clamp(state.progress - 0.27 * age);
    const frame = pathFrame(progress, width, height);
    const drift = (elapsed * (0.008 + seeded(index + 6) * 0.012) + index * 13) % 38;
    const lateral = (7 + seeded(index + 72) * 38 + drift) * (index % 2 ? 1 : -1);
    const along = (seeded(index + 99) - 0.5) * 26;
    const x = frame.x + frame.nx * lateral + frame.tx * along;
    const y = frame.y + frame.ny * lateral + frame.ty * along;
    const pulse = 0.45 + Math.sin(elapsed * 0.003 + index * 1.7) * 0.3;
    const alpha = clamp((1 - age) * pulse * state.residue, 0, 0.6);
    context.beginPath();
    context.arc(x, y, 0.45 + seeded(index + 121) * 1.25, 0, TAU);
    context.fillStyle = `rgba(179,239,255,${alpha})`;
    context.shadowBlur = 6;
    context.shadowColor = "rgba(91,207,247,.8)";
    context.fill();
  }
  context.restore();
}

function drawEmitter(context, width, height, state, elapsed) {
  if (state.energy <= 0.015) return;
  const head = pathFrame(state.progress, width, height);
  const intensity = state.energy * (0.88 + Math.sin(elapsed * 0.008) * 0.07 + Math.sin(elapsed * 0.021) * 0.035);
  const radius = 44 + intensity * 24;
  context.save();
  context.globalCompositeOperation = "lighter";
  const glow = context.createRadialGradient(head.x, head.y, 0, head.x, head.y, radius);
  glow.addColorStop(0, `rgba(255,255,255,${0.98 * intensity})`);
  glow.addColorStop(0.055, `rgba(228,252,255,${0.9 * intensity})`);
  glow.addColorStop(0.18, `rgba(135,228,255,${0.38 * intensity})`);
  glow.addColorStop(0.52, `rgba(49,166,215,${0.1 * intensity})`);
  glow.addColorStop(1, "rgba(20,108,164,0)");
  context.fillStyle = glow;
  context.fillRect(head.x - radius, head.y - radius, radius * 2, radius * 2);
  context.strokeStyle = `rgba(242,254,255,${0.54 * intensity})`;
  context.lineWidth = 0.8;
  context.shadowBlur = 7;
  context.shadowColor = "rgba(120,227,255,.9)";
  context.beginPath();
  context.moveTo(head.x - head.tx * 26, head.y - head.ty * 26);
  context.lineTo(head.x + head.tx * 18, head.y + head.ty * 18);
  context.moveTo(head.x - head.nx * 9, head.y - head.ny * 9);
  context.lineTo(head.x + head.nx * 9, head.y + head.ny * 9);
  context.stroke();
  context.restore();
}

export function createColdArchiveCompositor(canvas, { still = false } = {}) {
  if (!(canvas instanceof HTMLCanvasElement)) throw new TypeError("A canvas is required.");
  const context = canvas.getContext("2d", { alpha: true, desynchronized: true });
  if (!context) throw new Error("The cold archive compositor requires Canvas 2D support.");
  const reduced = matchMedia("(prefers-reduced-motion: reduce)");
  let width = 0;
  let height = 0;
  let dpr = 1;
  let frame = 0;
  let startedAt = performance.now();
  let pausedAt = 0;
  let pausedDuration = 0;
  let manuallyPaused = false;
  let destroyed = false;
  let lastDrawAt = 0;
  let lastState = { phase: "rest", progress: 0.015, energy: 0, residue: 0 };
  const staticState = Object.freeze({ phase: "traverse", progress: 0.57, energy: 0.72, residue: 0.78 });
  const layers = TERMS_SCENE.layers;

  function elapsed(now = performance.now()) {
    return Math.max(0, now - startedAt - pausedDuration);
  }

  function draw(state, time) {
    if (destroyed || !width || !height) return;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, width, height);
    drawVolumetricResponse(context, width, height, state);
    layers.forEach((layer) => drawPathStroke(context, width, height, state, time, layer));
    drawFilamentBranches(context, width, height, state, time);
    drawSecondaryMotes(context, width, height, state, time);
    drawEmitter(context, width, height, state, time);
    canvas.dataset.trailProgress = state.progress.toFixed(3);
    canvas.dataset.trailPhase = state.phase;
    lastState = state;
  }

  function resize() {
    const bounds = canvas.getBoundingClientRect();
    width = Math.max(1, Math.round(bounds.width));
    height = Math.max(1, Math.round(bounds.height));
    dpr = Math.min(devicePixelRatio || 1, TERMS_SCENE.performance.maxDevicePixelRatio);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    draw(still || reduced.matches ? staticState : cycleState(elapsed()), elapsed());
  }

  function tick(now) {
    frame = 0;
    if (destroyed || manuallyPaused || document.hidden || still || reduced.matches) return;
    if (now - lastDrawAt >= 1000 / TERMS_SCENE.performance.targetFps) {
      draw(cycleState(elapsed(now)), elapsed(now));
      lastDrawAt = now;
    }
    frame = requestAnimationFrame(tick);
  }

  function start() {
    if (destroyed || frame || manuallyPaused || document.hidden || still || reduced.matches) return;
    if (pausedAt) {
      pausedDuration += performance.now() - pausedAt;
      pausedAt = 0;
    }
    canvas.dataset.motion = "running";
    frame = requestAnimationFrame(tick);
  }

  function stop() {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    if (!pausedAt) pausedAt = performance.now();
  }

  function onVisibility() {
    if (document.hidden) stop();
    else start();
  }

  function onMotionChange() {
    if (reduced.matches) {
      stop();
      draw(staticState, 7800);
      canvas.dataset.motion = "static";
    } else start();
  }

  const observer = new ResizeObserver(resize);
  observer.observe(canvas);
  document.addEventListener("visibilitychange", onVisibility);
  reduced.addEventListener("change", onMotionChange);
  resize();
  start();

  return Object.freeze({
    toggle(forceRunning) {
      manuallyPaused = typeof forceRunning === "boolean" ? !forceRunning : !manuallyPaused;
      if (manuallyPaused) {
        stop();
        canvas.dataset.motion = still || reduced.matches ? "static" : "paused";
      } else {
        start();
        if (still || reduced.matches) canvas.dataset.motion = "static";
      }
      return !manuallyPaused;
    },
    getState() {
      return {
        renderer: "canvas-2d-layered",
        motion: still || reduced.matches ? "static" : manuallyPaused ? "paused" : "running",
        phase: lastState.phase,
        layers: Object.freeze(["source-core", "primary-filament", "secondary-branches-motes", "environmental-response", "decay-residue"]),
        mutation: false,
      };
    },
    destroy() {
      destroyed = true;
      stop();
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      reduced.removeEventListener("change", onMotionChange);
    },
  });
}
